import { useState } from 'react';
import { Button, Card, Stat, Toggle } from '@salonomy/ui';
import { useQueue, useToggleQueue } from '../data/queue.js';
import { useSalon, useStaff } from '../data/salon.js';
import { useServices } from '../data/services.js';
import { env } from '../lib/env.js';
import { useToast } from '../lib/toast.js';
import { JoinQueueModal } from '../features/queue/JoinQueueModal.js';
import { QueueTicketCard } from '../features/queue/QueueRail.js';

export function QueuePage() {
  const { data: queue } = useQueue();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: salon } = useSalon();
  const toggle = useToggleQueue();
  const toast = useToast();
  const [joinOpen, setJoinOpen] = useState(false);
  const tz = salon?.timezone ?? 'Europe/London';
  const tickets = queue?.tickets ?? [];
  const activeId = tickets.find((t) => t.status === 'waiting' || t.status === 'called')?.id;
  const publicUrl = `${env.queuePublicBaseUrl}/q/${salon?.slug ?? ''}`;

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Live queue</div>
          <div className="qrail__open">
            <span>{queue?.open ? 'Open' : 'Closed'}</span>
            <Toggle
              checked={Boolean(queue?.open)}
              onChange={(v) => toggle.mutate(v)}
              label="Toggle queue"
              labelHidden
            />
          </div>
        </div>
        <div className="topbar__right">
          <Button onClick={() => setJoinOpen(true)} disabled={!queue?.open}>
            + Add walk-in
          </Button>
        </div>
      </div>

      <div className="screen-scroll">
        <div className="screen-pad queue-page">
          <div>
            <Card padded>
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                <Stat label="Waiting" value={queue?.waitingCount ?? 0} />
                <Stat
                  label="Avg wait"
                  value={queue?.avgWaitMin != null ? `${queue.avgWaitMin}m` : '—'}
                />
                <Stat label="Tickets" value={tickets.length} />
              </div>
            </Card>

            <div className="qrail__list" style={{ marginTop: 18 }}>
              {tickets.length === 0 ? (
                <div className="muted">
                  {queue?.open ? 'No one waiting right now.' : 'The queue is closed.'}
                </div>
              ) : (
                tickets.map((t) => (
                  <QueueTicketCard
                    key={t.id}
                    ticket={t}
                    staff={staff ?? []}
                    services={services ?? []}
                    tz={tz}
                    active={t.id === activeId}
                  />
                ))
              )}
            </div>
          </div>

          <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="qshare">
              <span className="qshare__title">Share today’s queue</span>
              <div className="qshare__link">
                <span className="qshare__url">{publicUrl.replace(/^https?:\/\//, '')}</span>
                <button
                  className="qshare__copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(publicUrl);
                      toast.success('Queue link copied');
                    } catch {
                      toast.error('Could not copy');
                    }
                  }}
                >
                  Copy
                </button>
              </div>
              <span className="qshare__blurb">
                Clients see their place + ETA on their phone — no app, no login. Put it on
                Instagram, your Google profile and a window QR.
              </span>
            </div>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
            >
              Open public board →
            </a>
          </aside>
        </div>
      </div>

      {joinOpen ? (
        <JoinQueueModal
          staff={staff ?? []}
          services={services ?? []}
          onClose={() => setJoinOpen(false)}
        />
      ) : null}
    </>
  );
}
